import React, { useEffect, useRef } from 'react';

const SkipEyeLogoAnimated = ({ size = 'large' }) => {
  const logoRef = useRef(null);
  const leftPupilRef = useRef(null);
  const rightPupilRef = useRef(null);
  const animationRef = useRef(null);

  const dimensions = size === 'large' ? { width: 500, height: 200 } : { width: 300, height: 120 };

  useEffect(() => {
    let startTime = Date.now();

    const animateEyes = () => {
      if (!leftPupilRef.current || !rightPupilRef.current) return;

      const elapsed = Date.now() - startTime;
      const cycleDuration = 4000; // 4 seconds for full left-to-right-to-left cycle
      const progress = (elapsed % cycleDuration) / cycleDuration;

      // Use sine wave for smooth oscillation
      // Goes from -1 (left) to +1 (right) and back
      const angle = Math.sin(progress * Math.PI * 2) * Math.PI; // Oscillates between -PI and +PI
      const distance = 15; // Maximum distance from center

      const pupilX = Math.cos(angle) * distance;
      const pupilY = 0; // Keep vertical position centered

      const transform = `translate(calc(-50% + ${pupilX}px), calc(-50% + ${pupilY}px))`;
      leftPupilRef.current.style.transform = transform;
      rightPupilRef.current.style.transform = transform;

      animationRef.current = requestAnimationFrame(animateEyes);
    };

    animationRef.current = requestAnimationFrame(animateEyes);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const styles = {
    logoContainer: {
      position: 'relative',
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`,
      cursor: 'default'
    },
    skipContainer: {
      position: 'relative',
      width: '100%',
      height: '100%'
    },
    containerBody: {
      position: 'absolute',
      bottom: 0,
      left: '15%',
      right: '15%',
      height: '85%',
      background: 'linear-gradient(135deg, #7dd321 0%, #5cb41b 100%)',
      clipPath: 'polygon(0% 0%, 100% 0%, 80% 100%, 20% 100%)',
      borderRadius: '0 0 8px 8px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
    },
    containerRim: {
      position: 'absolute',
      bottom: '85%',
      left: '10%',
      right: '10%',
      height: '15%',
      background: 'linear-gradient(135deg, #8ee524 0%, #7dd321 100%)',
      borderRadius: '8px 8px 0 0',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
    },
    eye: {
      position: 'absolute',
      width: size === 'large' ? '70px' : '42px',
      height: size === 'large' ? '70px' : '42px',
      background: 'white',
      borderRadius: '50%',
      border: '4px solid #333',
      top: '45%',
      transform: 'translateY(-50%)',
      overflow: 'hidden',
      transition: 'all 0.1s ease-out'
    },
    leftEye: {
      left: '34.5%'
    },
    rightEye: {
      right: '34.5%'
    },
    pupil: {
      position: 'absolute',
      width: size === 'large' ? '40px' : '24px',
      height: size === 'large' ? '40px' : '24px',
      background: '#222',
      borderRadius: '50%',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      transition: 'all 0.15s ease-out'
    },
    highlight: {
      position: 'absolute',
      width: size === 'large' ? '14px' : '8px',
      height: size === 'large' ? '14px' : '8px',
      background: 'white',
      borderRadius: '50%',
      top: '30%',
      left: '35%',
      pointerEvents: 'none'
    }
  };

  return (
    <div ref={logoRef} style={styles.logoContainer}>
      <div style={styles.skipContainer}>
        <div style={styles.containerRim}></div>
        <div style={styles.containerBody}></div>

        <div style={{...styles.eye, ...styles.leftEye}}>
          <div ref={leftPupilRef} style={styles.pupil}>
            <div style={styles.highlight}></div>
          </div>
        </div>

        <div style={{...styles.eye, ...styles.rightEye}}>
          <div ref={rightPupilRef} style={styles.pupil}>
            <div style={styles.highlight}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkipEyeLogoAnimated;
